import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Bot, Sparkles, Users, Code, Mic, BarChart3 } from 'lucide-react'
import HeroSection from '../components/HeroSection'
import ScrollingTicker from '../components/ScrollingTicker'
import TestimonialCarousel from '../components/TestimonialCarousel'
import ProcessSteps from '../components/ProcessSteps'
import CTASection from '../components/CTASection'

const CapabilitiesSection = styled.section`
  padding: 96px 24px;
`

const CapInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const SectionLabel = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 12px;
  text-align: center;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
  text-align: center;
`

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  text-align: center;
  max-width: 600px;
  margin: 0 auto 56px;
  line-height: 1.7;
`

const CapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const CapCard = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 28px;
  transition: border-color 0.3s ease, transform 0.2s ease;

  &:hover {
    border-color: var(--color-accent);
    transform: translateY(-4px);
  }
`

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--color-accent-glow);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: var(--color-accent);
`

const CapTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 8px;
`

const CapDesc = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
`

const LogoBar = styled.section`
  padding: 48px 24px;
  border-top: 1px solid var(--color-border);
`

const LogoBarInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  text-align: center;
`

const LogoBarLabel = styled.p`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  margin-bottom: 24px;
`

const Logos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;
  flex-wrap: wrap;
  opacity: 0.6;

  img {
    height: 36px;
    filter: grayscale(100%) brightness(1.5);
    transition: filter 0.3s ease, opacity 0.3s ease;

    &:hover {
      filter: none;
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    gap: 32px;

    img {
      height: 28px;
    }
  }
`

const CAPABILITIES = [
  {
    icon: <Bot size={22} />,
    title: 'AI Customer Agents',
    desc: 'Intelligent chat agents that answer customer questions, route to humans when needed, and learn from your knowledge base.',
  },
  {
    icon: <Sparkles size={22} />,
    title: 'Social Content Automation',
    desc: 'AI-generated posts in your brand voice, pulled from your RSS feeds and industry news. Approval queue included.',
  },
  {
    icon: <Users size={22} />,
    title: 'Community Platforms',
    desc: 'Groups, invite codes, messaging, and engagement automation. Build a community around your brand.',
  },
  {
    icon: <Code size={22} />,
    title: 'Custom Development',
    desc: 'Web and mobile apps built on our production platform. Same infrastructure powering DoMoreWorld.',
  },
  {
    icon: <Mic size={22} />,
    title: 'Voice AI',
    desc: 'Voice-enabled agents for customer support. Real-time speech with natural language understanding.',
  },
  {
    icon: <BarChart3 size={22} />,
    title: 'Strategy & Operations',
    desc: '70+ years of combined experience in go-to-market, sales, branding, and business operations.',
  },
]

export default function Home() {
  return (
    <>
      <HeroSection />
      <ScrollingTicker />

      <CapabilitiesSection>
        <CapInner>
          <SectionLabel>What we deliver</SectionLabel>
          <SectionTitle>Technology meets expertise</SectionTitle>
          <SectionSubtitle>
            We don't just advise. We build, configure, and deploy AI-powered tools
            tailored to your business, then stay with you as it grows.
          </SectionSubtitle>
          <CapGrid>
            {CAPABILITIES.map((cap, i) => (
              <CapCard
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <IconWrap>{cap.icon}</IconWrap>
                <CapTitle>{cap.title}</CapTitle>
                <CapDesc>{cap.desc}</CapDesc>
              </CapCard>
            ))}
          </CapGrid>
        </CapInner>
      </CapabilitiesSection>

      <LogoBar>
        <LogoBarInner>
          <LogoBarLabel>Trusted by innovative companies</LogoBarLabel>
          <Logos>
            <img src="/images/logos/enginologi.jpg" alt="Enginologi" />
            <img src="/images/logos/mltwist.png" alt="MLTwist" />
            <img src="/images/logos/stealth.png" alt="Stealth Startup" />
            <img src="/images/logos/curious-creative.png" alt="Curious Creative" />
          </Logos>
        </LogoBarInner>
      </LogoBar>

      <ProcessSteps />
      <TestimonialCarousel />

      <CTASection
        title="Ready to grow intelligently?"
        subtitle="Book a free 30-minute discovery call. No pitch, no pressure; just a conversation about what's possible."
      />
    </>
  )
}
